var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Tooltip from 'devextreme/ui/tooltip';
import { v4 as uuidv4 } from 'uuid';
import { msg, updateWhenLocaleChanges } from '@lit/localize';
/**
 * Represents the multiple indicator card
 */
let DinapsisMultipleIndicator = class DinapsisMultipleIndicator extends LitElement {
    constructor() {
        super();
        /**
         * A property that is used to set the card width
         */
        this.width = '500px';
        /**
        * A property that is used to set the card height
        */
        this.height = 'auto';
        /**
        * Property used to set the text that will appear if there isn't data for the 'dataUnit' property.
        * Default value is 'sin datos'
        */
        this.textNoData = '';
        /**
        * Property used to set the color of texts and icons when there isn't data.
        * Default value is #e64949.
        */
        this.colorNoData = '#e64949';
        /**
         * Data.
         */
        this.data = {
            titleCard: '',
            clickableCard: false,
            topIconName: '',
            title: '',
            gridContainer: {
                numberColumns: 0,
                data: [],
            },
            complementaryText: {
                left: '',
                right: '',
            },
        };
        /**
         * A property that is used to set the component to show in mode card or not
         */
        this.isCard = true;
        /**
         * A property that is used to generate uniqueId
         */
        this.uniqueId = uuidv4();
        /**
         * A property that is used to save the list of tooltips
         */
        this.tooltipList = [];
        updateWhenLocaleChanges(this);
    }
    /**
     * The createRenderRoot() function
     * is a function that is called by the LitElement base class when the
     * element is first created. It returns the element itself,
     * which means that the element will be
     * rendered in place, rather than in a shadow DOM.
     * Helps to use the css.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * This function sets the number of rows and columns in the grid
     * based on the number of items in the data array.
     */
    setCardGrid() {
        const { numberColumns, data } = this.data.gridContainer;
        const numRows = Math.ceil(data.length / numberColumns);
        this.style.setProperty('--rows', numRows.toString());
        this.style.setProperty('--columns', numberColumns.toString());
    }
    /**
     * The above function is a lifecycle hook that is called when the component is updated.
     * It is used to initialize the card grid.
     */
    updated() {
        // First create the card grid
        this.setCardGrid();
        // Initialize all tooltips
        this.setupTooltips();
    }
    /**
     * A function that emit an event when the card icon is clicked
     */
    handleClickTopIcon(e) {
        if (!this.data.topIconName)
            return;
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('click-icon'));
    }
    /**
     * A function that emit an event when the card is clicked
     */
    handleCardClick(e) {
        if (!this.data.clickableCard)
            return;
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('click-card'));
    }
    /**
     * If the data.topIconName is not empty,
     * then return the html template literal.
     * @returns A string of HTML.
     */
    generateTopIcon() {
        if (!this.data.topIconName)
            return '';
        return html `
    <div class="multiple-indicator-container--top__icon">
      <span
        class="material-icons-round icon-top"
        @click=${this.handleClickTopIcon}
      >
        ${this.data.topIconName}
      </span>
    </div>
    `;
    }
    /**
     * A function that render a div container with all structure to create top container.
     * If it is not a card, nothing is returned
     * @returns Returns a html template.
     */
    generateTopIndicator() {
        if (!this.isCard)
            return html ``;
        const topIcon = this.generateTopIcon();
        return html `
      <div class="multiple-indicator-container--top">
        <div class="multiple-indicator-container--top__title" id="multiple-indicator-container--top__title-${this.uniqueId}">
          <span
          class="multiple-indicator-title">
            ${this.data.titleCard}
          </span>
        </div>

        ${topIcon}
      </div>
    `;
    }
    /**
     * For each item in the data array, create a div with a class of label-column,
     * and inside that div, create a span with a class of label, and inside that span,
     * insert the item's label property, and then create another span with a class of value,
     * and inside that span, insert the item's value property.
     * @returns A template literal with a div and a map function.
     */
    generateGridContainer() {
        return html `
    <div class="grid-container" style="height:${this.height}">
      ${this.data.gridContainer.data.map((item, key) => html `
        <div class="label-column">
          <div class="label-container" style="width: auto !important;" id="grid-label-${key}-${this.uniqueId}">
            <span class="label">${item.label}</span>
          </div>
          <div class="label-value-container" id="grid-label-value-${key}-${this.uniqueId}">
            <span class="value">${item.value}</span>
          </div>
        </div>
      `)}
      </div>
    `;
    }
    /**
     * If the data object has a complementaryText property
     * and that property has a left property, then
     * return a div with a span and another div.
     * @returns a string of HTML.
     */
    generateComplementaryTextLeft() {
        if (!this.data.complementaryText || !this.data.complementaryText.left)
            return '';
        return html `
    <div class="complementary-text-container" id="complementary-text-container-left-${this.uniqueId}">
      <span class="complementary-text complementary-text--left">
        ${this.data.complementaryText.left}
      </span>
    </div>
    `;
    }
    /**
     * If the data object has a complementaryText property
     * and that property has a right property, then
     * return a div with a span and another div.
     * @returns a string of HTML.
     */
    generateComplementaryTextRight() {
        if (!this.data.complementaryText || !this.data.complementaryText.right)
            return '';
        return html `
    <div class="complementary-text-container" id="complementary-text-container-right-${this.uniqueId}">
      <span class="complementary-text complementary-text--right">
        ${this.data.complementaryText.right}
      </span>
    </div>
    `;
    }
    /**
     * A function that recive one element and check if the text overflow or not.
     * @param el This param represents the element that will be checked if it overflows.
     * @returns Returns true if the element overflow and false if it doesn't.
     */
    checkOverflow(el) {
        const curOverflow = el.style.overflow;
        const element = el;
        const isOverflowing = el.clientWidth < el.scrollWidth;
        element.style.overflow = curOverflow;
        return isOverflowing;
    }
    /**
     * A function that recives two params and generate a tooltip.
     * @param tooltipId This param represents the id of the tooltip.
     * @param target This param represents the container the tooltip points to.
     */
    generateTooltip(tooltipId, target) {
        // Select and remove the previous tooltip
        const tooltipContainer = document.getElementById(tooltipId);
        if (tooltipContainer)
            tooltipContainer.remove();
        // Select the container where the tooltip will be painted
        const targetContainer = document.getElementById(target);
        if (!targetContainer)
            return;
        const txtTarget = targetContainer.textContent;
        // Create the tooltip div and set id and target text
        const newTooltipContainer = document.createElement('div');
        newTooltipContainer.id = tooltipId;
        newTooltipContainer.innerHTML = txtTarget !== null && txtTarget !== void 0 ? txtTarget : '';
        // Add to target the tooltip container
        targetContainer.append(newTooltipContainer);
        // Check if target needed tooltip and set or remove class
        if (targetContainer && !this.checkOverflow(targetContainer)) {
            newTooltipContainer.classList.add('hide-tooltip');
        }
        else {
            newTooltipContainer.classList.remove('hide-tooltip');
        }
        // Check if exist target, tooltip and tooltip doesn't have hide-tooltip class
        if (targetContainer
            && newTooltipContainer
            && !(newTooltipContainer === null || newTooltipContainer === void 0 ? void 0 : newTooltipContainer.classList.contains('hide-tooltip'))) {
            const tooltip = new Tooltip(newTooltipContainer);
            tooltip.option('target', targetContainer);
            tooltip.option('showEvent', 'mouseenter');
            tooltip.option('hideEvent', 'mouseleave');
        }
    }
    /**
     * It takes grid values and generates a tooltip object
     * @returns An array of objects with Tooltip options.
     */
    getTooltipGridContainer() {
        const tooltip = [];
        if (!this.data.gridContainer)
            return tooltip;
        this.data.gridContainer.data.forEach((_i, key) => {
            tooltip.push({ tooltipId: `tooltip-multiple-indicator-label-${key}-${this.uniqueId}`, target: `grid-label-${key}-${this.uniqueId}` });
            tooltip.push({ tooltipId: `tooltip-multiple-indicator-label-value-${key}-${this.uniqueId}`, target: `grid-label-value-${key}-${this.uniqueId}` });
        });
        return tooltip;
    }
    /**
     * A function that setup the tooltips of the component
     */
    setupTooltips() {
        const cardContainer = document.querySelector(`[data-id="${this.uniqueId}"]`);
        const tooltipOptions = [
            {
                tooltipId: `tooltip-multiple-indicator-title-${this.uniqueId}`,
                target: `multiple-indicator-container--top__title-${this.uniqueId}`,
            },
            {
                tooltipId: `tooltip-multiple-indicator-description-${this.uniqueId}`,
                target: `multiple-indicator-description-container-${this.uniqueId}`,
            },
            {
                tooltipId: `tooltip-multiple-indicator-complementary-text-container-left-${this.uniqueId}`,
                target: `complementary-text-container-left-${this.uniqueId}`,
            },
            {
                tooltipId: `tooltip-multiple-indicator-complementary-text-container-right-${this.uniqueId}`,
                target: `complementary-text-container-right-${this.uniqueId}`,
            },
        ];
        const tooltipGrid = this.getTooltipGridContainer();
        const newTooltipOptions = [...tooltipOptions, ...tooltipGrid];
        this.tooltipList = newTooltipOptions;
        if (!cardContainer)
            return;
        this.tooltipList.forEach((t) => this.generateTooltip(t.tooltipId, t.target));
    }
    /**
    * Render
    */
    render() {
        const topContainer = this.generateTopIndicator();
        const gridContainer = this.generateGridContainer();
        const complementaryLeft = this.generateComplementaryTextLeft();
        const complementaryRight = this.generateComplementaryTextRight();
        const multipleComplementary = complementaryLeft !== '' && complementaryRight !== '';
        let classContainer = 'multiple-indicator-container';
        if (this.isCard)
            classContainer += ' dinapsis-card dinapsis-card-shadowed';
        if (this.data.clickableCard)
            classContainer += ' clickable-card';
        const complementaryClass = multipleComplementary ? 'bottom-complementary-container--multiple' : 'bottom-complementary-container--single';
        const textNoData = this.textNoData === '' ? msg('text_no_data') : this.textNoData;
        return html `
    <div data-id="${this.uniqueId}" class="${classContainer}" @click=${this.handleCardClick} style="width: ${this.width}; height:${this.height}">
      ${topContainer}
      <div class="multiple-indicator-description-container" id="multiple-indicator-description-container-${this.uniqueId}">
        <span
        class="multiple-indicator-description"
        style="color: ${this.data.title === '' ? this.colorNoData : ''}">
          ${this.data.title === '' ? textNoData : this.data.title}
        </span>
        <div id="tooltip-multiple-indicator-description-${this.uniqueId}">${this.data.title === '' ? textNoData : this.data.title}</div>
      </div>
      ${gridContainer}
      <div class="bottom-complementary-container ${complementaryClass}">
        ${complementaryLeft}
        ${complementaryRight}
      </div>
    </div>
    `;
    }
};
__decorate([
    state(),
    property({ type: String })
], DinapsisMultipleIndicator.prototype, "width", void 0);
__decorate([
    state(),
    property({ type: String })
], DinapsisMultipleIndicator.prototype, "height", void 0);
__decorate([
    property({ type: String })
], DinapsisMultipleIndicator.prototype, "textNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisMultipleIndicator.prototype, "colorNoData", void 0);
__decorate([
    state(),
    property({ type: Array })
], DinapsisMultipleIndicator.prototype, "data", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisMultipleIndicator.prototype, "isCard", void 0);
DinapsisMultipleIndicator = __decorate([
    customElement('dinapsis-multiple-indicator')
], DinapsisMultipleIndicator);
export { DinapsisMultipleIndicator };
//# sourceMappingURL=dinapsis-multiple-indicator.js.map